import * as yup from 'yup';
import { isNil } from 'lodash';
import { startOfDay, subYears, addDays, isMatch, isDate } from 'date-fns';
import {
  CLIENT_DATE_FORMAT,
  GENDER,
  NATIONALITY_CONSTANTS,
} from 'src/constants';
import {
  DOC_TYPES,
  TOURIST_FIELDS as T,
  WRONG_FORMAT_MESSAGES,
} from '../store/constants/tourists';

import { pluralizeYears } from '../../../utils/pluralizeUtils';
import { convertClientDateToServerDate } from '../../../utils/dateUtils';

const DEFAULT_DATE_TYPE_ERROR = 'Некорректный формат даты';

/**
 * Date with format validation.
 * @return Date
 */
yup.addMethod(yup.date, 'format', function format() {
  return this.transform((value, originalValue) => {
    if (!originalValue) {
      return undefined;
    }

    if (isDate(originalValue)) return originalValue;

    const isValidClientData = isMatch(originalValue, CLIENT_DATE_FORMAT);
    if (isValidClientData) {
      return new Date(convertClientDateToServerDate(originalValue));
    }

    // default return invalid Date
    return new Date('');
  }).typeError(DEFAULT_DATE_TYPE_ERROR);
});

const getDateSchema = (label, emptyMessage) =>
  yup.date().label(label).format().required(emptyMessage);

const getMinDateForAge = (age, tourEndDate) => {
  const date = new Date(tourEndDate);
  const formattedStart = startOfDay(date);
  const formattedYear = subYears(formattedStart, age + 1);
  const result = addDays(formattedYear, 1);

  return result;
};
const getMaxDateForAge = (age, tourEndDate) => {
  const date = new Date(tourEndDate);
  const formattedStart = startOfDay(date);
  const result = subYears(formattedStart, age);

  return result;
};
// Проверка ФИО
const validateName = (docType, fieldName) => {
  const message = WRONG_FORMAT_MESSAGES[fieldName];
  const { pattern } = DOC_TYPES[docType];
  return yup
    .string()
    .trim()
    .required(message)
    .typeError(message)
    .matches(pattern, message);
};
export const validateAge = (age, tourEndDate, isLevelTravelTourOperator) => {
  const birthDaySchema = getDateSchema(
    'Дата рождения',
    'Введите дату рождения',
  );
  if (isNil(age)) {
    // Взрослый
    const adultMinAge = isLevelTravelTourOperator ? 17 : 12;
    const WRONG_ADULT_AGE_MESSAGE = `Взрослый турист должен быть старше ${adultMinAge} ${pluralizeYears(
      adultMinAge,
    )} на момент окончания тура`;
    return birthDaySchema.max(
      getMaxDateForAge(adultMinAge, tourEndDate),
      WRONG_ADULT_AGE_MESSAGE,
    );
  }
  // Ребенок
  const WRONG_CHILD_AGE_MESSAGE = `Ребёнку должно быть ${age} ${pluralizeYears(
    age,
  )} на момент окончания тура`;
  // It shouldn't return brand new date schema, but it does not work otherwise, see: https://github.com/jquense/yup/issues/255
  return birthDaySchema
    .min(getMinDateForAge(age, tourEndDate), WRONG_CHILD_AGE_MESSAGE)
    .max(getMaxDateForAge(age, tourEndDate), WRONG_CHILD_AGE_MESSAGE);
};

export const makeTouristSchema = ({ tourEndDate, isLevelTravelTourOperator }) =>
  yup.object({
    [T.DOC_TYPE]: yup.mixed().oneOf(Object.keys(DOC_TYPES)).required(),
    [T.NAME]: yup
      .string()
      .when('docType', (docType) => validateName(docType, T.NAME)),
    [T.SURNAME]: yup
      .string()
      .when('docType', (docType) => validateName(docType, T.SURNAME)),
    [T.PATROMYMIC]: yup
      .string()
      .trim()
      .when('docType', (docType, schema) => {
        const required = docType !== DOC_TYPES.international_passport.id;
        return required
          ? validateName(docType, T.PATROMYMIC)
          : schema.nullable();
      }),
    [T.BIRTHDAY]: yup
      .mixed()
      .when('age', (age) =>
        validateAge(age, tourEndDate, isLevelTravelTourOperator),
      ),
    [T.GENDER]: yup
      .mixed()
      .oneOf(Object.values(GENDER), 'Укажите пол')
      .required('Укажите пол'),
    [T.CITIZENSHIP]: yup.number().integer().required(),
    hideDocInputs: yup.boolean(),
    [T.DOC_NUMBER]: yup
      .mixed()
      .when(
        ['hideDocInputs', 'nationality_id', 'docType'],
        (hideDocInputs, nationality_id, docType, schema) => {
          if (hideDocInputs) {
            return schema.nullable();
          }
          const documentNumberSchema = yup
            .string()
            .trim()
            .required('Введите номер документа');
          const { pattern } = DOC_TYPES[docType].numberField;

          // Only use validation by pattern for russian citizens
          const usePattern = nationality_id === NATIONALITY_CONSTANTS.RU;
          return usePattern && pattern
            ? documentNumberSchema.matches(
                pattern,
                'Некорректный номер документа',
              )
            : documentNumberSchema;
        },
      ),
    [T.DOC_EXPIRES_DATE]: yup.mixed().when(['hideDocInputs', 'docType'], {
      is: (hideDocInputs, docType) =>
        hideDocInputs || docType !== DOC_TYPES.international_passport.id,
      then: yup.mixed().nullable(),
      otherwise: () =>
        yup
          .date()
          .label('Дата истечения загранпаспорта')
          .format()
          .min(tourEndDate, 'Загранпаспорт истекает раньше даты окончания тура')
          .required('Введите дату истечения загранпаспорта'),
    }),
    // [T.VISA_SUPPORT]: props.hasVisa ? VISA_SERVICES.NO_VISA.id : undefined,
  });

export const makeTouristsSchema = (touristSchema) =>
  yup.array().of(touristSchema);

export const makeTouristSchemaHotelCheckout = (supplierFields) =>
  yup.object({
    [T.DOC_TYPE]: yup.mixed().oneOf(Object.keys(DOC_TYPES)).required(),
    [T.NAME]: yup
      .string()
      .when('docType', (docType) => validateName(docType, T.NAME)),
    [T.SURNAME]: yup
      .string()
      .when('docType', (docType) => validateName(docType, T.SURNAME)),
    [T.PATROMYMIC]: supplierFields?.patronymic
      ? yup
          .string()
          .trim()
          .when('docType', (docType, schema) => {
            const required = docType !== DOC_TYPES.international_passport.id;
            return required
              ? validateName(docType, T.PATROMYMIC)
              : schema.nullable();
          })
      : undefined,
    [T.GENDER]: supplierFields?.gender
      ? yup
          .mixed()
          .oneOf(Object.values(GENDER), 'Укажите пол')
          .required('Укажите пол')
      : undefined,
  });
